/**
 * Publications/Research API → normalized intelligence graph adapter.
 *
 * Transforms Publications search results (stData records) into graph nodes
 * and provenance-preserving edges:
 * - authors            → person nodes
 * - affiliations       → organization / PLA organization nodes
 * - publications       → research (evidence) nodes
 * - keywords/topKeywords → technology nodes
 *
 * Risk records classify organizations rather than becoming graph nodes: a
 * risk record with `collection = "People's Liberation Army"` (or Extreme
 * risk, which Data Abyss derives PLA entities from — see PLA Yellow Pages)
 * classifies the referenced affiliation as a PLA/military organization.
 *
 * Tolerant of inconsistent historical fields: both `affiliation` and the
 * upstream-typo `afffiliation` on author_ records, each of which may be a
 * string or an array.
 */
import { SearchApi as PublicationsSearchApi } from '../../api/publications/search';
import ISTData from '../../api/models/ISTData';
import { CriteriaData, Field } from '../../api/models/CriteriaData';
import { IntelEvidence } from '../../types/intelligenceGraph';
import { IntelligenceGraphBuilder } from './graphBuilder';
import {
  looksLikePlaOrganization,
  makeNodeId,
  normalizeEntityName,
} from './entityResolver';

/** Default number of results per expansion, per the analyst workflow spec. */
export const DEFAULT_PUBLICATIONS_PAGE_SIZE = 100;

/** Bounds so one dense publication cannot explode the graph. */
const MAX_AUTHORS_PER_PUBLICATION = 15;
const MAX_AFFILIATIONS_PER_PUBLICATION = 12;
const MAX_KEYWORDS_PER_PUBLICATION = 10;
const MAX_COLLABORATION_PAIRS = 15;

export type PublicationsSearchKind = 'organization' | 'person' | 'technology';

const SEARCH_FIELD: Record<PublicationsSearchKind, Field> = {
  organization: 'affiliations',
  person: 'author_.name',
  technology: 'keywords',
};

/** stData records also carry `topKeywords` on some historical documents. */
type LoosePublication = ISTData & { topKeywords?: string[] };

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (v): v is string => typeof v === 'string' && !!v.trim(),
    );
  }
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

/**
 * Internal Data Abyss publication route (same one PLA Yellow Pages uses):
 * `/publication/[id]` renders the publication record inside the existing
 * Database/Publications interface. External links (doi/url/source) stay in
 * node meta but never drive the primary Open Publication action.
 */
function publicationUrl(pub: LoosePublication): string | undefined {
  if (pub.id) return `/publication/${encodeURIComponent(pub.id)}`;
  return undefined;
}

function publicationEvidence(pub: LoosePublication): IntelEvidence {
  return {
    sourceDataset: 'publications',
    sourceRecordId: pub.id,
    evidence: pub.title,
    url: publicationUrl(pub),
    detail: {
      year: pub.year ? String(pub.year) : undefined,
      origin: pub.origin,
    },
  };
}

/**
 * Extracts a PLA classification reason from a publication's risk records for
 * the given affiliation, or undefined when the affiliation carries no
 * military risk on this record.
 */
function plaClassification(
  pub: LoosePublication,
  affiliation: string,
): string | undefined {
  const normalized = normalizeEntityName(affiliation);
  const risks = pub.risk ?? [];
  const match = risks.find((risk) => {
    const riskAffiliations = asStringArray(risk.affiliations).map(
      normalizeEntityName,
    );
    const appliesToAffiliation =
      riskAffiliations.length === 0 || riskAffiliations.includes(normalized);
    if (!appliesToAffiliation) return false;
    return (
      risk.collection === "People's Liberation Army" || risk.level === 'Extreme'
    );
  });
  if (match) {
    return match.collection === "People's Liberation Army"
      ? `Risk collection "People's Liberation Army" (${match.level})`
      : `Extreme risk classification${
          match.collection ? ` (${match.collection})` : ''
        }`;
  }
  if (looksLikePlaOrganization(normalized)) {
    return 'PLA/military name pattern';
  }
  return undefined;
}

/**
 * Normalizes the author_ affiliation field, tolerating `affiliation`,
 * the upstream typo `afffiliation`, and string-or-array values.
 */
function authorAffiliations(author: {
  affiliation?: string | string[];
  afffiliation?: string | string[];
}): string[] {
  return [
    ...asStringArray(author.affiliation),
    ...asStringArray(author.afffiliation),
  ];
}

/**
 * Adds one Publications stData record and all of its observed/derived
 * relationships to the builder.
 */
export function addPublicationToGraph(
  builder: IntelligenceGraphBuilder,
  pub: LoosePublication,
): void {
  if (!pub.id || !pub.title) return;

  const evidence = publicationEvidence(pub);
  const researchId = makeNodeId('research', pub.id);
  builder.upsertNode({
    id: researchId,
    type: 'research',
    label: pub.title,
    dataset: 'publications',
    evidence,
    meta: {
      publicationId: pub.id,
      year: pub.year ? String(pub.year) : undefined,
      doi: pub.doi,
      abstract: pub.abstract ? pub.abstract.slice(0, 500) : undefined,
    },
  });

  // --- Organizations (affiliations) ---
  const affiliations = asStringArray(pub.affiliations).slice(
    0,
    MAX_AFFILIATIONS_PER_PUBLICATION,
  );
  const orgIds: string[] = [];
  affiliations.forEach((affiliation) => {
    const pla = plaClassification(pub, affiliation);
    const node = builder.upsertNode({
      type: pla ? 'pla' : 'organization',
      label: affiliation,
      dataset: 'publications',
      evidence,
      plaEvidence: pla,
    });
    orgIds.push(node.id);
    builder.upsertEdge({
      source: node.id,
      target: researchId,
      type: 'MENTIONED_IN',
      sourceDataset: 'publications',
      sourceRecordId: pub.id,
      evidence: pub.title,
      url: evidence.url,
      relationshipType: 'observed',
      confidence: 1,
    });
  });

  // --- People (authors) ---
  const structuredAuthors = (pub.author_ ?? []).filter((a) => a.name);
  const flatAuthors = asStringArray(pub.authors).map((name) => ({ name }));
  const authors = (
    structuredAuthors.length > 0 ? structuredAuthors : flatAuthors
  ).slice(0, MAX_AUTHORS_PER_PUBLICATION);

  const personIds: string[] = [];
  authors.forEach((author) => {
    const name = normalizeEntityName(author.name as string);
    if (!name) return;
    const person = builder.upsertNode({
      type: 'person',
      label: name,
      dataset: 'publications',
      evidence,
    });
    personIds.push(person.id);
    builder.upsertEdge({
      source: person.id,
      target: researchId,
      type: 'AUTHORED',
      sourceDataset: 'publications',
      sourceRecordId: pub.id,
      evidence: pub.title,
      url: evidence.url,
      relationshipType: 'observed',
      confidence: 1,
    });

    // Direct author→affiliation observations (both spellings, both shapes).
    authorAffiliations(author as Record<string, string | string[]>).forEach(
      (affiliation) => {
        const pla = plaClassification(pub, affiliation);
        const org = builder.upsertNode({
          type: pla ? 'pla' : 'organization',
          label: affiliation,
          dataset: 'publications',
          plaEvidence: pla,
        });
        builder.upsertEdge({
          source: person.id,
          target: org.id,
          type: 'AFFILIATED_WITH',
          sourceDataset: 'publications',
          sourceRecordId: pub.id,
          evidence: pub.title,
          url: evidence.url,
          relationshipType: 'observed',
          confidence: 1,
        });
      },
    );
  });

  // Flat-author records carry no per-author affiliation: derive membership
  // from the publication's affiliation list at reduced confidence.
  if (structuredAuthors.length === 0 && orgIds.length > 0) {
    personIds.forEach((personId) => {
      orgIds.forEach((orgId) => {
        builder.upsertEdge({
          source: personId,
          target: orgId,
          type: 'AFFILIATED_WITH',
          sourceDataset: 'publications',
          sourceRecordId: pub.id,
          evidence: pub.title,
          url: evidence.url,
          relationshipType: 'derived',
          confidence: 0.5,
        });
      });
    });
  }

  // --- Technology (keywords / topKeywords) ---
  const keywords = Array.from(
    new Set(
      [...asStringArray(pub.keywords), ...asStringArray(pub.topKeywords)].map(
        normalizeEntityName,
      ),
    ),
  ).slice(0, MAX_KEYWORDS_PER_PUBLICATION);
  keywords.forEach((keyword) => {
    if (!keyword) return;
    const tech = builder.upsertNode({
      type: 'technology',
      label: keyword,
      dataset: 'publications',
      evidence,
    });
    builder.upsertEdge({
      source: researchId,
      target: tech.id,
      type: 'RELATED_TECHNOLOGY',
      sourceDataset: 'publications',
      sourceRecordId: pub.id,
      evidence: pub.title,
      url: evidence.url,
      relationshipType: 'observed',
      confidence: 1,
    });
    // Organizations research the technologies on their publications (derived).
    orgIds.forEach((orgId) => {
      builder.upsertEdge({
        source: orgId,
        target: tech.id,
        type: 'RESEARCHES',
        sourceDataset: 'publications',
        sourceRecordId: pub.id,
        evidence: pub.title,
        url: evidence.url,
        relationshipType: 'derived',
        confidence: 0.7,
      });
    });
  });

  // --- Co-affiliation collaboration (derived) ---
  let pairs = 0;
  for (
    let i = 0;
    i < orgIds.length && pairs < MAX_COLLABORATION_PAIRS;
    i += 1
  ) {
    for (
      let j = i + 1;
      j < orgIds.length && pairs < MAX_COLLABORATION_PAIRS;
      j += 1
    ) {
      builder.upsertEdge({
        source: orgIds[i],
        target: orgIds[j],
        type: 'COLLABORATED_WITH',
        sourceDataset: 'publications',
        sourceRecordId: pub.id,
        evidence: pub.title,
        url: evidence.url,
        relationshipType: 'derived',
        confidence: 0.7,
      });
      pairs += 1;
    }
  }
}

/**
 * Searches the Publications API for an entity and merges every returned
 * publication into the builder. Returns the number of records processed and
 * the reported total so the UI can offer "load more".
 */
export async function searchPublicationsIntoGraph(
  builder: IntelligenceGraphBuilder,
  query: string,
  kind: PublicationsSearchKind,
  size: number = DEFAULT_PUBLICATIONS_PAGE_SIZE,
  offset = 0,
): Promise<{ processed: number; total: number }> {
  const criteria: CriteriaData[] = [
    {
      q: query,
      field: SEARCH_FIELD[kind],
      accuracy: 'Accurate',
      operator: 'OR',
    },
  ];
  const response = await PublicationsSearchApi.generalSearch(
    criteria,
    offset,
    // eslint-disable-next-line camelcase
    { publication_date: 'desc' },
    size,
  );
  const publications = (response.data.stData ?? []) as LoosePublication[];
  publications.forEach((pub) => addPublicationToGraph(builder, pub));
  return {
    processed: publications.length,
    total: response.data.totalPublications ?? publications.length,
  };
}
